import { bookList } from 'src/constants/bookList';
import { isRegisteredAppPath } from 'src/routing/isRegisteredAppPath';

export const AUTH_RETURN_TO_QUERY_PARAM = 'returnTo';

const PENDING_AUTH_RETURN_TO_STORAGE_KEY = 'lyt.auth.pendingReturnTo';

/**
 * True when the login route is handling an Auth0 authorization-code callback.
 *
 * @param {URLSearchParams|Readonly<URLSearchParams>} searchParams
 * @returns {boolean}
 */
export const isAuth0CallbackSearch = (searchParams) => (
    Boolean(searchParams?.has?.('code') && searchParams?.has?.('state'))
);

/**
 * Auth0 logout returnTo for the login route, preserving deep-link query params.
 *
 * @param {string|URLSearchParams} [search]
 * @returns {string}
 */
export const buildAuthLoginLogoutReturnTo = (search = '') => {
    const serializedSearch = search instanceof URLSearchParams
        ? search.toString()
        : (search.startsWith('?') ? search.slice(1) : search);
    const normalizedSearch = serializedSearch ? `?${serializedSearch}` : '';

    return `${window.location.origin}${bookList.auth.login}${normalizedSearch}`;
};

/**
 * Auth0 callback URL for the login route. Must match Auth0Provider's redirect_uri
 * and the application's Allowed Callback URLs in the Auth0 dashboard.
 */
export const getAuthLoginRedirectUri = () => `${window.location.origin}${bookList.auth.login}`;

/**
 * Allow only in-app registered routes to prevent open redirects.
 *
 * @param {string} returnPath - Path with optional query string (e.g. `/requests`)
 * @returns {boolean}
 */
export const isSafeAuthReturnPath = (returnPath) => {
    if (!returnPath || typeof returnPath !== 'string') {
        return false;
    }

    if (!returnPath.startsWith('/') || returnPath.startsWith('//')) {
        return false;
    }

    if (
        returnPath === bookList.auth.login
        || returnPath.startsWith(`${bookList.auth.login}?`)
        || returnPath.startsWith(`${bookList.auth.login}/`)
    ) {
        return false;
    }

    const [pathname] = returnPath.split('?');

    return isRegisteredAppPath(pathname);
};

/**
 * Build the login route search string, preserving a safe post-auth destination.
 *
 * @param {string} pathname
 * @param {string} [search]
 * @returns {string}
 */
export const buildAuthLoginSearch = (pathname, search = '') => {
    const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;
    const returnPath = normalizedSearch ? `${pathname}?${normalizedSearch}` : pathname;

    if (!isSafeAuthReturnPath(returnPath)) {
        return normalizedSearch;
    }

    const params = new URLSearchParams(normalizedSearch);
    params.set(AUTH_RETURN_TO_QUERY_PARAM, returnPath);

    return params.toString();
};

/**
 * Persist a post-auth destination across the Auth0 redirect when the login URL
 * no longer carries `returnTo` (callback URLs only include `code` and `state`).
 *
 * @param {string} returnPath
 * @returns {void}
 */
export const setPendingAuthReturnTo = (returnPath) => {
    if (typeof window === 'undefined' || !isSafeAuthReturnPath(returnPath)) {
        return;
    }

    window.sessionStorage.setItem(PENDING_AUTH_RETURN_TO_STORAGE_KEY, returnPath);
};

/**
 * Read and clear a pending post-auth destination, if one exists.
 *
 * @returns {string|undefined}
 */
export const consumePendingAuthReturnTo = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    const pendingReturnTo = window.sessionStorage.getItem(PENDING_AUTH_RETURN_TO_STORAGE_KEY);

    if (!pendingReturnTo) {
        return undefined;
    }

    window.sessionStorage.removeItem(PENDING_AUTH_RETURN_TO_STORAGE_KEY);

    return isSafeAuthReturnPath(pendingReturnTo) ? pendingReturnTo : undefined;
};

/**
 * Resolve where to send the user after Auth0 login completes.
 *
 * @param {Object} [options]
 * @param {URLSearchParams|Readonly<URLSearchParams>} [options.searchParams]
 * @param {{ returnTo?: string }|undefined} [options.appState]
 * @returns {string}
 */
export const resolvePostAuthRedirectPath = ({ searchParams, appState } = {}) => {
    const returnToFromQuery = searchParams?.get?.(AUTH_RETURN_TO_QUERY_PARAM);

    if (returnToFromQuery && isSafeAuthReturnPath(returnToFromQuery)) {
        return returnToFromQuery;
    }

    const returnToFromAppState = appState?.returnTo;

    if (typeof returnToFromAppState === 'string' && isSafeAuthReturnPath(returnToFromAppState)) {
        return returnToFromAppState;
    }

    const pendingReturnTo = consumePendingAuthReturnTo();

    if (pendingReturnTo) {
        return pendingReturnTo;
    }

    return '/';
};
